import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AddressPropertyEditor } from '../AddressPropertyEditor'
import { JsonPropertyDisplay } from '../JsonPropertyDisplay'
import { EnumPropertyDisplay, EnumPropertyEditInput, getBooleanDisplay, getBooleanOptions } from './PropertyInputUtils'
import { SuggestedTextInput } from './SuggestedTextInput'
import type { PropertyDefinition } from '../../hooks/useQueries'
import { formatDisplayNumber } from '../../lib/format'

/**
 * Single owner of the property-value display / edit type dispatch.
 *
 * Asset, document and user property tables all render their value cells through this module.
 * Before it existed the dispatch was copy-pasted in `AssetProperties.tsx` only, which is why
 * object-valued properties (ISO 20022 addresses, JSON) rendered as raw blobs on the document
 * and user pages and, worse, seeded `[object Object]` into a plain text input when edited.
 */

/** A neighbouring property of the same entity, used to resolve the address trio. */
export interface PropertySibling {
    /** Property definition code, e.g. `address_raw`. */
    code: string
    /**
     * Whatever key the host table uses to address the property in its save callback —
     * the property definition UID for assets/documents, the code for users.
     */
    key: string
    value: unknown
}

/** Saves one property value. May be called several times for a single edit (address trio). */
export type SavePropertyFn = (propertyKey: string, value: unknown) => Promise<void>

/**
 * True when the value must be edited through a dedicated dialog rather than the inline
 * scalar inputs. Callers MUST consult this before entering inline edit mode: it is the guard
 * that keeps an object value from ever being stringified into a text input.
 */
export function usesDialogEditor(propDef: PropertyDefinition | undefined, value: unknown): boolean {
    if (propDef?.code === 'address_iso20022') return true
    if (propDef?.data_type === 'json') return true
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Scalar value formatting shared by the three property tables (array join, boolean semantic,
 * date, number + unit). Object values are never expected here — they are routed to a dialog
 * editor by `PropertyValueDisplay` — but they degrade to JSON rather than `[object Object]`.
 */
export function formatPropertyValue(
    value: unknown,
    propDef: PropertyDefinition | undefined,
    tCommon: (key: string) => string,
): ReactNode {
    if (value === null || value === undefined) return 'N/A'

    if (Array.isArray(value)) {
        return value.join(', ')
    }
    if (typeof value === 'object') return JSON.stringify(value)

    if (propDef) {
        // Boolean values with semantic display
        if (propDef.data_type === 'boolean' && (typeof value === 'string' || typeof value === 'boolean')) {
            const boolValue = typeof value === 'boolean' ? value : value === 'true'
            const semantic = propDef.properties?.semantic as string
            return getBooleanDisplay(boolValue, semantic, tCommon)
        }

        // Date values
        if (propDef.data_type === 'date' && value && typeof value === 'string') {
            try {
                const date = new Date(value)
                if (!isNaN(date.getTime())) {
                    if (propDef.properties?.with_time === true) {
                        return date.toLocaleString()
                    }
                    return date.toLocaleDateString()
                }
            } catch {
                // Fall back to string representation if parsing fails
            }
        }

        // Number values with units
        if (propDef.data_type === 'number') {
            const numVal = typeof value === 'number' ? value : parseFloat(value as string)
            if (!isNaN(numVal)) {
                if (propDef.properties?.unit) {
                    return `${formatDisplayNumber(numVal)} ${propDef.properties.unit}`
                }
                return formatDisplayNumber(numVal)
            }
        }
    }

    // No definition, or a definition that does not call itself a number, yet the
    // value arrived as one anyway (a derived aggregate, a legacy text property
    // holding a float). The one-decimal rule is about what the reader sees, not
    // about what the catalogue claims, so it applies here too.
    if (typeof value === 'number') return formatDisplayNumber(value)

    return String(value)
}

export interface PropertyValueDisplayProps {
    org: string
    value: unknown
    propertyDefinition?: PropertyDefinition
    /** Key the host table uses in `saveProperty` (definition UID, or code for users). */
    propertyKey: string
    /** Other properties of the same entity — used to write the address trio together. */
    siblings?: PropertySibling[]
    /** Forced readonly. `system_managed` on the definition also forces it. */
    readonly?: boolean
    saveProperty: SavePropertyFn
    /** Rendered when the value is null/undefined (host's "click to edit" hint). */
    fallback?: ReactNode
}

/** Read-only rendering of a property value, with dialogs for object-valued properties. */
export function PropertyValueDisplay({
    org,
    value,
    propertyDefinition: propDef,
    propertyKey,
    siblings = [],
    readonly,
    saveProperty,
    fallback = null,
}: PropertyValueDisplayProps) {
    const { t: tCommon } = useTranslation('common')

    if (value === null || value === undefined) return <>{fallback}</>

    const isReadonly = Boolean(readonly) || Boolean(propDef?.flags?.includes('system_managed'))

    // ISO 20022 address — dedicated dialog editor writing the address trio together.
    if (propDef?.code === 'address_iso20022') {
        const addressRawSibling = siblings.find(s => s.code === 'address_raw')
        const geolocationSibling = siblings.find(s => s.code === 'geolocation')
        const geoValue = geolocationSibling?.value as Record<string, any> | null | undefined
        const parsedGeo = geoValue && typeof geoValue === 'object' && 'lat' in geoValue && 'lng' in geoValue
            ? { lat: Number(geoValue.lat), lng: Number(geoValue.lng) }
            : null

        return (
            <AddressPropertyEditor
                value={value as Record<string, any> | null}
                addressRaw={typeof addressRawSibling?.value === 'string' ? addressRawSibling.value : null}
                geolocation={parsedGeo}
                readonly={isReadonly}
                onSave={async (data) => {
                    await saveProperty(propertyKey, data.iso20022)
                    // Siblings are optional: `address_raw` / `geolocation` definitions do not
                    // target every entity type (documents have neither), so skip what is absent
                    // instead of failing the whole save.
                    if (addressRawSibling) {
                        await saveProperty(addressRawSibling.key, data.raw)
                    }
                    if (geolocationSibling && data.geolocation) {
                        await saveProperty(geolocationSibling.key, data.geolocation)
                    }
                }}
            />
        )
    }

    // A site's second frontage: the same address dialog, alone (the raw line and the
    // geolocation describe the main address).
    if (propDef?.code === 'secondary_address_iso20022') {
        return (
            <AddressPropertyEditor
                value={value as Record<string, any> | null}
                addressRaw={null}
                geolocation={null}
                readonly={isReadonly}
                onSave={async (data) => { await saveProperty(propertyKey, data.iso20022) }}
            />
        )
    }

    // JSON properties (and any stray object value) — structured JSON editor.
    if (propDef?.data_type === 'json' || (typeof value === 'object' && !Array.isArray(value))) {
        return (
            <JsonPropertyDisplay
                value={value}
                propertyDefinition={propDef}
                readonly={isReadonly}
                onChange={isReadonly ? undefined : async (newValue) => {
                    try {
                        await saveProperty(propertyKey, newValue)
                    } catch (error) {
                        console.error('Failed to save JSON property:', error)
                    }
                }}
            />
        )
    }

    // Enum properties with colors / emojis
    if (propDef?.data_type === 'enum' && propDef.uid) {
        return <EnumPropertyDisplay propertyDefinitionUid={propDef.uid} value={value as string | string[]} org={org} />
    }

    const displayValue = formatPropertyValue(value, propDef, tCommon)

    // Text property carrying a link pattern
    if (propDef?.data_type === 'text' && typeof propDef.properties?.link_pattern === 'string' && typeof value === 'string') {
        let link: string | null = null
        try {
            const candidate = propDef.properties.link_pattern.replace('{value}', encodeURIComponent(value))
            if (candidate.startsWith('http://') || candidate.startsWith('https://')) {
                link = candidate
            }
        } catch (e) {
            console.warn('Failed to construct link from pattern:', propDef.properties.link_pattern, 'with value:', value, e)
        }
        if (link) {
            return (
                <div className="flex items-center gap-2">
                    <span>{displayValue}</span>
                    <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-primary hover:text-primary/80 underline text-sm"
                        title={`Open ${link}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        🔗
                    </a>
                </div>
            )
        }
    }

    return <span>{displayValue}</span>
}

export interface PropertyValueEditInputProps {
    org: string
    propertyDefinition?: PropertyDefinition
    value: string
    onChange: (value: string) => void
    onKeyDown: (e: React.KeyboardEvent) => void
    disabled?: boolean
    hasError?: boolean
    /**
     * Type of the asset being edited. Feeds the value suggestions of a text
     * property whose definition declares `properties.suggestions` with the
     * default `asset_type` scope (spec 2026-09-02-03).
     */
    assetTypeUid?: string
    /**
     * The edited asset's other values, keyed by property code, so a suggestion
     * list declaring `depends_on` can narrow itself (models of the brand
     * already set on the asset).
     */
    propertyContext?: Record<string, string>
    /** Overrides the default `property-edit-*` testid of the rendered input. */
    'data-testid'?: string
    autoFocus?: boolean
}

/**
 * Inline scalar edit input (boolean / date / enum / number / text). Object-valued properties
 * never reach this component — `usesDialogEditor` keeps them on the dialog path.
 */
export function PropertyValueEditInput({
    org,
    propertyDefinition: propDef,
    value,
    onChange,
    onKeyDown,
    disabled = false,
    hasError = false,
    assetTypeUid,
    propertyContext,
    'data-testid': dataTestId,
    autoFocus,
}: PropertyValueEditInputProps) {
    const { t } = useTranslation('properties')
    const { t: tCommon } = useTranslation('common')

    // Regression guard: a stringified object must never be seeded into a text input.
    if (import.meta.env.DEV && /^\[object [A-Za-z]+\]$/.test(value)) {
        console.error(
            'PropertyValueEditInput received a stringified object — object properties must use the dialog editors (see usesDialogEditor)',
            { code: propDef?.code, value },
        )
    }

    if (propDef?.data_type === 'boolean') {
        const semantic = propDef.properties?.semantic as string | undefined
        const options = getBooleanOptions(semantic, tCommon)
        return (
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={onKeyDown}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                data-testid="property-edit-boolean"
            >
                {options.map(option => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        )
    }

    if (propDef?.data_type === 'date') {
        const withTime = propDef.properties?.with_time === true
        return (
            <input
                type={withTime ? 'datetime-local' : 'date'}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={onKeyDown}
                onFocus={(e) => (e.currentTarget as any).showPicker?.()}
                className={`flex-1 px-2 py-1 border ${hasError ? 'border-destructive' : 'border-border'} rounded text-sm bg-background text-foreground`}
                disabled={disabled}
                autoFocus
                data-testid="property-edit-date"
            />
        )
    }

    if (propDef?.data_type === 'enum' && propDef.uid) {
        return (
            <EnumPropertyEditInput
                propertyDefinitionUid={propDef.uid}
                value={value}
                onChange={onChange}
                disabled={disabled}
                hasError={hasError}
                org={org}
            />
        )
    }

    if (propDef?.data_type === 'number') {
        const base = propDef.properties?.integer_only ? t('value_input.enter_integer') : t('value_input.enter_number')
        const unit = propDef.properties?.unit
        return (
            <input
                type="number"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={onKeyDown}
                step={propDef.properties?.integer_only ? '1' : 'any'}
                min={typeof propDef.properties?.min_value === 'number' ? propDef.properties.min_value : undefined}
                max={typeof propDef.properties?.max_value === 'number' ? propDef.properties.max_value : undefined}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder={`${base}${unit ? ` (${unit})` : ''}...`}
                data-testid="property-edit-number"
            />
        )
    }

    if (propDef?.data_type === 'text') {
        // Opt-in free-text combobox: the value stays free text, existing values
        // are just one keystroke away (spec 2026-09-02-03 §4).
        if (propDef.properties?.suggestions) {
            return (
                <SuggestedTextInput
                    org={org}
                    propertyDefinition={propDef}
                    assetTypeUid={assetTypeUid}
                    context={propertyContext}
                    value={value}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    disabled={disabled}
                    hasError={hasError}
                    data-testid={dataTestId ?? 'property-edit-text'}
                    autoFocus={autoFocus}
                />
            )
        }

        const hasPatternError = Boolean(propDef.properties?.regex_pattern && value && (() => {
            try {
                const regex = new RegExp(propDef.properties!.regex_pattern as string)
                return !regex.test(value)
            } catch {
                return false
            }
        })())

        return (
            <div className="flex-1">
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={onKeyDown}
                    className={`flex h-9 w-full rounded-md border ${hasPatternError ? 'border-destructive' : 'border-input'} bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring`}
                    placeholder={t('value_input.enter_text')}
                    disabled={disabled}
                    autoFocus={autoFocus}
                    data-testid={dataTestId ?? 'property-edit-text'}
                />
                {hasPatternError ? (
                    <p className="text-xs text-destructive mt-1">
                        {t('value_input.pattern_constraint', { pattern: (propDef.properties?.regex_pattern as string) || '' })}
                    </p>
                ) : null}
            </div>
        )
    }

    return (
        <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            placeholder={t('value_input.enter_value')}
            data-testid="property-edit-value"
        />
    )
}
